import React, { useState } from "react";
import { MdVerified, MdScience, MdBuild, MdStar } from "react-icons/md";
import { ChangeLogsTile } from "./ChangeLogsTile";
import { TabsContainer, TabsHeader, TabItem, TabContent } from "./styled";

const FAVORITE_CHANGELOGS_KEY = "favoriteChangelogsIds";
const LTS_VERSION_PREFIX = "5.5";
const VERSION_DESCRIPTIONS = {
  DEV: "Wersja deweloperska do bieżących prac i szybkiej weryfikacji zmian.",
  BETA: "Wersja przedprodukcyjna do testów funkcjonalnych przed pełnym wdrożeniem.",
  PROD: "Wersja produkcyjna do stabilnej, codziennej pracy na środowiskach klienta.",
  LTS: "Stabilna wersja długoterminowa z wydłużonym wsparciem w zakresie poprawek krytycznych błędów i bezpieczeństwa.",
  OBSERWOWANE: "Lista zmian oznaczonych gwiazdką, aby łatwo wrócić do najważniejszych wpisów.",
};

export const ChangesLog = ({ data }) => {
  const [activeTab, setActiveTab] = useState("DEV");
  const [favoriteKeys, setFavoriteKeys] = useState(() => {
    if (typeof window === "undefined") {
      return [];
    }

    const savedFavorites = localStorage.getItem(FAVORITE_CHANGELOGS_KEY);

    if (!savedFavorites) {
      return [];
    }

    try {
      const parsedFavorites = JSON.parse(savedFavorites);
      return Array.isArray(parsedFavorites) ? parsedFavorites : [];
    } catch {
      return [];
    }
  });

  const changeLogs = data?.changelogs || [];
  const isLtsVersion = (version) => version?.startsWith(LTS_VERSION_PREFIX);

  const getChangeLogKey = (item) => {
    if (item?.id !== undefined && item?.id !== null && item?.id !== "") {
      return `id:${item.id}`;
    }

    if (item?.link) {
      return `link:${item.link}`;
    }

    return `fallback:${item?.repoName || ""}|${item?.description || ""}|${item?.pamBuildVersion || ""}|${item?.pamBuildVersionBeta || ""}|${item?.pamBuildVersionDev || ""}`;
  };

  const sortByVersionDesc = (list, versionKey) =>
    list.sort((a, b) => {
      const parseVersion = (version) => {
        if (!version) return [0, 0, 0];
        const parts = version.split(".").map((part) => parseInt(part, 10) || 0);
        return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
      };

      const [majorA, minorA, patchA] = parseVersion(a[versionKey]);
      const [majorB, minorB, patchB] = parseVersion(b[versionKey]);

      if (majorA !== majorB) return majorB - majorA;
      if (minorA !== minorB) return minorB - minorA;
      return patchB - patchA;
    });

  const prodChangeLogs = sortByVersionDesc(
    changeLogs.filter((item) => item.pamBuildVersion && !isLtsVersion(item.pamBuildVersion)),
    "pamBuildVersion"
  );
  const ltsChangeLogs = sortByVersionDesc(
    changeLogs.filter((item) => isLtsVersion(item.pamBuildVersion)),
    "pamBuildVersion"
  );
  const betaChangeLogs = sortByVersionDesc(
    changeLogs.filter((item) => item.pamBuildVersionBeta),
    "pamBuildVersionBeta"
  );
  const devChangeLogs = sortByVersionDesc(
    changeLogs.filter((item) => item.pamBuildVersionDev),
    "pamBuildVersionDev"
  );
  const favoriteChangeLogs = changeLogs.filter((item) => favoriteKeys.includes(getChangeLogKey(item)));

  const toggleFavorite = (changeLogKey) => {
    setFavoriteKeys((previousFavorites) => {
      const isFavorite = previousFavorites.includes(changeLogKey);
      const nextFavorites = isFavorite
        ? previousFavorites.filter((favoriteKey) => favoriteKey !== changeLogKey)
        : [...previousFavorites, changeLogKey];

      if (typeof window !== "undefined") {
        localStorage.setItem(FAVORITE_CHANGELOGS_KEY, JSON.stringify(nextFavorites));
      }

      return nextFavorites;
    });
  };

  const renderContent = () => {
    switch (activeTab) {
      case "DEV":
        return (
          <ChangeLogsTile
            list={devChangeLogs}
            tileHeader="Lista Zmian - wersja DEV"
            tileDescription={VERSION_DESCRIPTIONS.DEV}
            versionType="pamBuildVersionDev"
            favoriteKeys={favoriteKeys}
            onToggleFavorite={toggleFavorite}
            getItemKey={getChangeLogKey}
          />
        );
      case "BETA":
        return (
          <ChangeLogsTile
            list={betaChangeLogs}
            tileHeader="Lista Zmian - wersja BETA"
            tileDescription={VERSION_DESCRIPTIONS.BETA}
            versionType="pamBuildVersionBeta"
            favoriteKeys={favoriteKeys}
            onToggleFavorite={toggleFavorite}
            getItemKey={getChangeLogKey}
          />
        );
      case "PROD":
        return (
          <ChangeLogsTile
            list={prodChangeLogs}
            tileHeader="Lista Zmian - wersja PROD"
            tileDescription={VERSION_DESCRIPTIONS.PROD}
            versionType="pamBuildVersion"
            favoriteKeys={favoriteKeys}
            onToggleFavorite={toggleFavorite}
            getItemKey={getChangeLogKey}
          />
        );
      case "LTS":
        return (
          <ChangeLogsTile
            list={ltsChangeLogs}
            tileHeader="Lista Zmian - wersja LTS"
            tileDescription={VERSION_DESCRIPTIONS.LTS}
            versionType="pamBuildVersion"
            favoriteKeys={favoriteKeys}
            onToggleFavorite={toggleFavorite}
            getItemKey={getChangeLogKey}
          />
        );
      case "OBSERWOWANE":
        return (
          <ChangeLogsTile
            list={favoriteChangeLogs}
            tileHeader="Lista obserwowanych zmian"
            tileDescription={VERSION_DESCRIPTIONS.OBSERWOWANE}
            versionType="currentVersion"
            favoriteKeys={favoriteKeys}
            onToggleFavorite={toggleFavorite}
            getItemKey={getChangeLogKey}
          />
        );
      default:
        return null;
    }
  };

  if (changeLogs.length === 0) {
    return null;
  }

  return (
    <TabsContainer>
      <TabsHeader>
        <TabItem $active={activeTab === "DEV"} onClick={() => setActiveTab("DEV")}>
          <MdBuild /> DEV
        </TabItem>
        <TabItem $active={activeTab === "BETA"} onClick={() => setActiveTab("BETA")}>
          <MdScience /> BETA
        </TabItem>
        <TabItem $active={activeTab === "PROD"} onClick={() => setActiveTab("PROD")}>
          <MdVerified /> PROD
        </TabItem>
        <TabItem $active={activeTab === "LTS"} onClick={() => setActiveTab("LTS")}>
          <MdVerified /> LTS
        </TabItem>
        <TabItem $active={activeTab === "OBSERWOWANE"} onClick={() => setActiveTab("OBSERWOWANE")}>
          <MdStar /> OBSERWOWANE
        </TabItem>
      </TabsHeader>
      <TabContent>{renderContent()}</TabContent>
    </TabsContainer>
  );
};
